import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const Support = () => {
    const [amount, setAmount] = useState("");
    const [firstName, setFirstName] = useState("");
    const [recurring, setRecurring] = useState(false);
    const [loading, setLoading] = useState(false);

    const numeric = parseFloat(amount);
    const canGive = !Number.isNaN(numeric) && numeric >= 1;

    const donate = async () => {
        if (!canGive) { toast("Please enter any amount you wish", { description: "Even a dollar keeps a candle lit." }); return; }
        setLoading(true);
        try {
            const r = await api.post("/payments/donate", {
                amount: numeric,
                recurring,
                first_name: firstName.trim() || null,
                origin_url: window.location.origin,
            });
            window.location.href = r.data.checkout_url;
        } catch (e) {
            toast.error("Unable to open checkout", { description: e?.response?.data?.detail || "Please try again." });
            setLoading(false);
        }
    };

    return (
        <div className="max-w-3xl mx-auto px-6 py-20 text-center" data-testid="support-page">
            <div className="small-caps text-xs text-muted-foreground mb-3">A quiet way to help</div>
            <h1 className="font-serif text-4xl md:text-5xl">A Free-Will Offering</h1>
            <p className="mt-6 text-muted-foreground max-w-xl mx-auto italic leading-relaxed">
                Everything here — the prayers, the voice, the Companion — is offered freely and always will be.
                If it has been a grace to you, an offering of any size keeps the candles lit.
            </p>
            <p className="mt-4 text-xs small-caps accent-liturgy">Give whatever you wish. Nothing is expected.</p>

            <div className="mt-12 missal-card p-8 md:p-12 text-left">
                <label htmlFor="offering-name" className="small-caps text-[11px] accent-liturgy mb-3 block">Your first name (optional)</label>
                <input
                    id="offering-name"
                    type="text"
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    data-testid="donor-first-name"
                    placeholder="So we may thank you personally"
                    className="w-full bg-transparent border-b border-border pb-2 font-body text-base focus:outline-none focus:border-liturgy placeholder:italic placeholder:text-muted-foreground/50"
                />

                <label htmlFor="offering-amount" className="mt-8 small-caps text-[11px] accent-liturgy mb-3 block">Your offering</label>
                <div className="flex items-center gap-3 border-b border-border pb-3 focus-within:border-liturgy transition-colors">
                    <span className="font-serif text-4xl text-muted-foreground">$</span>
                    <input
                        id="offering-amount"
                        type="number" min="1" step="1"
                        value={amount}
                        onChange={e => setAmount(e.target.value)}
                        data-testid="amount-custom"
                        placeholder="Whatever moves you"
                        className="flex-1 bg-transparent font-serif text-4xl focus:outline-none placeholder:text-muted-foreground/50 placeholder:font-body placeholder:text-base placeholder:italic"
                    />
                    {amount && (
                        <button type="button" onClick={() => setAmount("")} data-testid="amount-clear"
                            className="text-xs small-caps text-muted-foreground hover:accent-liturgy px-2">
                            clear
                        </button>
                    )}
                </div>

                <label className="mt-8 flex items-center gap-3 text-sm text-muted-foreground cursor-pointer">
                    <input type="checkbox" checked={recurring} onChange={e => setRecurring(e.target.checked)} data-testid="recurring-toggle" />
                    <span>Make this a monthly offering</span>
                </label>

                <button onClick={donate} disabled={loading || !canGive} data-testid="donate-btn"
                    className="mt-8 w-full py-4 bg-liturgy text-white small-caps disabled:opacity-30 disabled:cursor-not-allowed transition-opacity">
                    {loading ? "Opening…" : (canGive ? (recurring ? `Give $${numeric} each month` : `Offer $${numeric}`) : "Enter any amount above")}
                </button>
                <p className="mt-4 text-xs text-muted-foreground text-center">
                    Secure checkout via Stripe · Card · Apple Pay · Google Pay
                </p>
            </div>

            <p className="mt-10 text-xs text-muted-foreground italic max-w-md mx-auto">
                “The widow put in more than all the others” — even the smallest offering, given freely, is enough.
            </p>
        </div>
    );
};

export default Support;
